/**
 * `dismissedGaps` on a present_plan resume — how the officer clears one of the
 * AI Assistant's clarifying questions instead of answering it ("this doesn't
 * apply here").
 *
 * The dismissal is permanent for the thread and enforced server-side: the
 * questions are recorded on the thread and every later present_plan call has
 * matching gaps stripped from its arguments, so a dismissed question never
 * returns in a later review, in the approved plan's `<gaps>` section, or in a
 * chained full-report run — even reworded. Matching is on the description
 * text, because gaps carry no id.
 *
 * Accepted on both resume paths (approval and Request Changes), de-duplicated
 * server-side, and a no-op for a gap the agent never raised — so each gap only
 * needs to be sent once, on the response where the officer cleared it.
 */

/** Max entries the server reads off one `dismissedGaps` payload. */
export const MAX_DISMISSED_GAPS = 50;

/** Max description length the server reads per entry. */
export const MAX_DISMISSED_GAP_DESCRIPTION = 300;

export interface DismissedGapPayload {
  category?: string;
  description: string;
}

interface DismissableGap {
  category?: string | null;
  description?: string | null;
}

/**
 * The wire shape for the gaps the officer struck out. Only `category` and
 * `description` are read, so nothing else is sent.
 *
 * The server applies the 50 × 300 caps when it *reads* the field — but it
 * persists the raw payload as sent, so they're enforced here instead of relying
 * on that truncation.
 */
export const buildDismissedGapsPayload = (
  gaps: ReadonlyArray<DismissableGap>,
): DismissedGapPayload[] =>
  gaps
    .flatMap((gap) => {
      const description = gap.description?.trim();
      if (!description) return [];
      const category = gap.category?.trim();
      return [
        {
          ...(category ? { category } : {}),
          description: description.slice(0, MAX_DISMISSED_GAP_DESCRIPTION),
        },
      ];
    })
    .slice(0, MAX_DISMISSED_GAPS);

/**
 * Identity for a gap in client-side dismissal state, for surfaces whose gaps
 * have no id of their own (the chat plan card reads them straight off
 * `toolArgs`). Normalized the same way the server matches: on the description
 * text alone, case- and whitespace-insensitive.
 */
export const gapDismissalKey = (description: string): string =>
  description.trim().replace(/\s+/g, " ").toLowerCase();
